import { BlockBlobClient } from "@azure/storage-blob";
import type { AnyFieldApi } from "@tanstack/react-form";
import { type ClassValue, clsx } from "clsx";
import type {
  Option,
  TypeaheadPropsAndState,
} from "react-bootstrap-typeahead/types/types";
import { v4 as uuidv4 } from "uuid";
import type { Jurisdiction } from "@/briefpoint-client";
import {
  type Case,
  type CaseFileApi,
  type CaseFileUpload,
  type CaseFileUploadBatch,
  CommunicationStatus,
  type DocumentDetail,
  DocumentStatus,
  DocumentSubType,
  DocumentType,
  type FormInt,
  type Interrogatory,
  PartyPosition,
  PropoundingParty,
  SubscriptionPlan,
  SubscriptionStatus,
  type ToggleableFeature,
  type User,
} from "@/briefpoint-client";
import type { CombinedCaseSearch } from "@/components/ConfirmInfo/ReviewPageOne";
import {
  FILE_EXPLORER_VARIANTS,
  GOTENBURG_MIME_TYPES,
  NO_CASE_TEXT,
  PRODUCE_DOCUMENTS_OBJECTION_ID,
} from "@/lib/constants";
import type {
  CaseResponse,
  DefinitionInclusionMode,
  DocumentClient,
  DocumentDetailClient,
  DocumentResponse,
  ExternalCaseResponse,
  FileExplorerVariant,
  FirmClient,
  FirmResponse,
  JurisdictionResponse,
  UserResponse,
  ViewCommunicationResponse,
} from "@/lib/types";
import {
  API_URL,
  CaliforniaId,
  NewYorkId,
  PETITIONER_AND_RESPONDENT_TYPES,
  SEARCH_PARAMS,
  SHARE_SITES,
} from "./constants";

/**
 * Utility function to merge class names using clsx
 */
export function cn(...inputs: ClassValue[]) {
  return clsx(inputs);
}

/**
 * Maps a party's `PartyPosition` to display labels, using petitioner/respondent
 * wording for family law and hyphenated cross-party labels.
 */
export function formatPartyPositionForDisplay(
  position: PartyPosition | undefined,
  isFamilyLawCase: boolean,
): PartyPosition | string | undefined {
  if (!position) return position;
  if (isFamilyLawCase) {
    if (position === PartyPosition.Defendant) {
      return PETITIONER_AND_RESPONDENT_TYPES.RESPONDENT;
    }
    if (position === PartyPosition.Plaintiff) {
      return PETITIONER_AND_RESPONDENT_TYPES.PETITIONER;
    }
    if (position === PartyPosition.CrossDefendant) {
      return PETITIONER_AND_RESPONDENT_TYPES.CROSS_RESPONDENT;
    }
    if (position === PartyPosition.CrossComplainant) {
      return PETITIONER_AND_RESPONDENT_TYPES.CROSS_PETITIONER;
    }
    return position;
  }
  if (position === PartyPosition.CrossDefendant) {
    return PETITIONER_AND_RESPONDENT_TYPES.CROSS_DEFENDANT;
  }
  if (position === PartyPosition.CrossComplainant) {
    return PETITIONER_AND_RESPONDENT_TYPES.CROSS_COMPLAINANT;
  }
  return position;
}

/**
 * Converts any error type to a proper Error object with a stack trace.
 * Prevents "Script error" messages in Raygun by ensuring all errors have:
 * - Descriptive error messages
 * - Proper Error instance
 * - Stack trace
 */
export function normalizeError(error: unknown): Error {
  let result: Error;

  // Already an Error - return as-is
  if (error instanceof Error) {
    result = error;
  }
  // HTTP Response objects (from fetch/API failures)
  else if (error instanceof Response) {
    result = new Error(
      `HTTP ${error.status} ${error.statusText || "Error"}: ${error.url || "Unknown URL"}`,
    );
    result.name = "HTTPError";
  }
  // Plain objects
  else if (typeof error === "object" && error !== null) {
    // Object with message property - extract it
    if ("message" in error && typeof error.message === "string") {
      result = new Error(error.message);
      if ("name" in error && typeof error.name === "string") {
        result.name = error.name;
      }
    }
    // Other objects - serialize
    else {
      try {
        result = new Error(`Error object: ${JSON.stringify(error)}`);
        result.name = "SerializedError";
      } catch {
        result = new Error("[Circular reference or non-serializable data]");
        result.name = "UnserializableError";
      }
    }
  }
  // Primitives (string, number, boolean, null, undefined)
  else {
    result = new Error(String(error) || "Unknown error");
    result.name = "PrimitiveError";
  }

  // Ensure stack trace exists (prevents "Script error" in Raygun)
  if (!result.stack) {
    if (Error.captureStackTrace) {
      Error.captureStackTrace(result, normalizeError);
    } else {
      result.stack = `${result.name}: ${result.message}\n  at normalizeError`;
    }
  }

  return result;
}

/**
 * Generate a url for an api call using a base path and a dictionary of parameters.
 */
export function getUrl(
  endpoint: string,
  params: Record<string, string | string[]>,
): string {
  const url = new URL(`${API_URL}/api/${endpoint}`);
  if (params) {
    for (const key in params) {
      if (params[key] != null) {
        if (Array.isArray(params[key])) {
          for (const element of params[key]) {
            url.searchParams.append(key, element);
          }
        } else {
          url.searchParams.append(key, params[key]);
        }
      }
    }
  }
  return url.toString();
}

export const groupBy = <T>(list: T[], getKey: (item: T) => string) =>
  list.reduce(
    (previous, currentItem) => {
      const group = getKey(currentItem);
      if (!previous[group]) previous[group] = [];
      previous[group].push(currentItem);
      return previous;
    },
    {} as Record<string, T[]>,
  );

export function sanitizeFiles(
  files: File[],
  includeImages = false,
  includeZipFiles = false,
) {
  const acceptableFileTypes = [
    "application/pdf",
    ...(includeZipFiles
      ? ["application/zip", "application/x-zip-compressed"]
      : []),
    //"application/vnd.openxmlformats-officedocument.wordprocessingml.document", //TODO: will add this back in once the backend is updated to support it
    //"application/msword", //TODO: will add this back in once the backend is updated to support it
  ];
  if (includeImages) {
    acceptableFileTypes.push(
      "image/gif",
      "image/jpg",
      "image/jpeg",
      "image/png",
    );
  }
  return files.filter((file) => acceptableFileTypes.includes(file.type));
  // TODO: check files for ??
}

/**
 * Returns the file extension from a filename (e.g. "Report.pages" → ".pages").
 * Used as a fallback when validating uploads so we can accept files by extension
 * when the browser reports a wrong or empty MIME type.
 */
function getFileExtension(name: string): string {
  const lastDot = name.lastIndexOf(".");
  return lastDot === -1 ? "" : `.${name.slice(lastDot + 1).toLowerCase()}`;
}

/**
 * Filters files to those supported by Gotenburg (document conversion).
 * A file is accepted if (1) its reported MIME type is in the allowed list, or
 * (2) its extension is in the allowed list. The extension check is a fallback
 * for when browsers send a wrong or generic MIME type (e.g. .pages often
 * reported as application/octet-stream or empty string).
 */
export function sanitizeGottenburgFiles(files: File[], includeImages = false) {
  const mimeTypeEntries = Object.entries(GOTENBURG_MIME_TYPES);
  const acceptableMimeTypes = [
    ...mimeTypeEntries
      .filter(([mime]) => includeImages || !mime.startsWith("image/"))
      .map(([mime]) => mime),
    "application/x-zip-compressed",
  ];
  const acceptableExtensions = mimeTypeEntries
    .filter(([mime]) => includeImages || !mime.startsWith("image/"))
    .flatMap(([, exts]) => exts);
  return files.filter((file) => {
    if (acceptableMimeTypes.includes(file.type)) return true;
    const ext = getFileExtension(file.name);
    return acceptableExtensions.includes(ext);
  });
}

/**
 * Splits files into supported (by Gotenburg) and unsupported. Use when you need
 * to upload only supported files and show a warning for unsupported ones.
 */
export function getSupportedAndUnsupportedFiles(
  files: File[],
  includeImages = false,
): { supported: File[]; unsupported: File[] } {
  const supported = sanitizeGottenburgFiles(files, includeImages);
  const supportedSet = new Set(supported);
  const unsupported = files.filter((f) => !supportedSet.has(f));
  return { supported, unsupported };
}

/**
 *
 *
 * Subscription utils
 *
 *
 */
export function hasActiveSubscription(firm: FirmClient) {
  return (
    firm &&
    firm.billingInfo.subscriptionStatus === SubscriptionStatus.Active &&
    firm.billingInfo.subscriptionPlan !== SubscriptionPlan.None
  );
}

export function hasCanceledSubscription(firm: FirmClient) {
  return firm?.billingInfo?.subscriptionStatus === SubscriptionStatus.Canceled;
}

export function isInTrial(firm: FirmClient, mustBeActive = false) {
  return (
    firm?.billingInfo?.subscriptionStatus === SubscriptionStatus.Trialing &&
    (!mustBeActive ||
      (firm?.billingInfo?.trialEnds &&
        firm.billingInfo.trialEnds > new Date()) ||
      firm?.billingInfo?.creditsAvailable)
  );
}

export function isInTrialOrHasActiveSubscription(firm: FirmClient) {
  return isInTrial(firm, true) || hasActiveSubscription(firm);
}

/**
 *
 *
 * Response transformers
 *
 *
 */

/**
 * Add uploadDate to the document if it's not present.
 */
export function addUploadDate(
  document: DocumentResponse | DocumentDetail,
): DocumentClient | DocumentDetailClient {
  const uploadDate = document.uploadDate ? document.uploadDate : new Date();
  return {
    ...document,
    uploadDate,
  };
}

export function transformFirm(firm: FirmResponse): FirmClient {
  return {
    ...firm,
    address: firm.address || {},
    billingInfo: {
      ...firm.billingInfo,
      trialEnds: firm.billingInfo?.trialEnds
        ? new Date(firm.billingInfo?.trialEnds)
        : undefined,
    },
  };
}

export function getSubTypeText(
  subType: DocumentSubType,
  jurisdictionId: string,
) {
  switch (subType) {
    case DocumentSubType.SpecialInterrogatories:
      if (jurisdictionId === CaliforniaId) {
        return {
          plural: "Special Interrogatories",
          singular: "Special Interrogatory",
          request: "Special Interrogatory",
          response: "Interrogatory",
          short: "SROG",
        };
      }
      return {
        plural: "Interrogatories",
        singular: "Interrogatory",
        request: "Interrogatory",
        response: "Interrogatory",
        short: "ROG",
      };
    case DocumentSubType.RequestsForAdmission:
      if (jurisdictionId === NewYorkId) {
        return {
          plural: "Notice to Admit",
          singular: "Notice to Admit",
          request: "Notice to Admit",
          response: "Notice to Admit",
          short: "NTA",
        };
      }
      return {
        plural: "Requests for Admission",
        singular: "Request for Admission",
        request: "Request for Admission",
        response: "Request for Admission",
        short: "RFA",
      };
    case DocumentSubType.RequestsForProduction:
      if (jurisdictionId === NewYorkId) {
        return {
          plural: "Notice of Discovery & Inspection",
          singular: "Notice of Discovery & Inspection",
          request: "Notice of Discovery & Inspection",
          response: "Notice of Discovery & Inspection",
          short: "NDI",
        };
      }
      return {
        plural: "Requests for Production",
        singular: "Request for Production",
        request: "Request for Production",
        response: "Request for Production",
        short: "RFP",
      };

    case DocumentSubType.Complaint:
      return {
        plural: "Complaints",
        singular: "Complaint",
        request: "",
        response: "Answer",
        short: "Complaint",
      };
    case DocumentSubType.Petition:
      return {
        plural: "Petitions",
        singular: "Petition",
        request: "",
        response: "Answer",
        short: "Petition",
      };
    case DocumentSubType.FormInterrogatories:
      return {
        plural: "Form Interrogatories",
        singular: "Form Interrogatory",
        request: "Form Interrogatory",
        response: "Interrogatory",
        short: "Form Rog",
      };
    default:
      return {
        plural: "Unknown",
        singular: "Unknown",
        request: "",
        response: "",
        short: "",
      };
  }
}

/**
 * Get the display number for an interrogatory/request
 */
export function getInterrogatoryDisplayNumber(
  document: DocumentDetailClient,
  interrogatory: Interrogatory,
  formInterrogatory: FormInt | null,
): string {
  if (
    document.info?.documentSubType === DocumentSubType.FormInterrogatories &&
    formInterrogatory
  ) {
    return (
      formInterrogatory.displayName ||
      `${formInterrogatory.section}.${formInterrogatory.number}`
    );
  }

  const number =
    ((document.info?.startingNumber ?? 0) > 1
      ? interrogatory.number + ((document.info?.startingNumber ?? 0) - 1)
      : interrogatory.number) ?? 1;

  return number.toString();
}

/**
 * Calculate font size for interrogatory numbers based on digit count.
 * Numbers shrink as they grow longer to fit within circular buttons.
 * @param value - The number or string to calculate font size for
 * @returns Font size in pixels (12px for 1 digit, decreasing by 1px per additional digit, minimum 6px)
 */
export function getFontSize(value: string | number): number {
  // Count only digits, excluding decimals and other characters
  const digitCount = String(value).replace(/\D/g, "").length;
  // 12px for 1 digit, decreasing by 1px per additional digit with minimum of 6px
  return Math.max(12 - (digitCount - 1), 6);
}

export function formatLongDate(
  date: Date,
  optionOverrides: Intl.DateTimeFormatOptions | undefined = {},
) {
  const options: Intl.DateTimeFormatOptions | undefined = {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    timeZoneName: "short",
    ...optionOverrides,
  };
  return toLocaleUSDateString(date, options);
}

/**
 * Tests provided UserAgent against Known Mobile User Agents
 */
export const isMobile = () =>
  /Mobile|iP(hone|od|ad)|Android|BlackBerry|IEMobile/.test(
    window.navigator.userAgent || window.navigator.vendor,
  );

/**
 * Tests userAgent to see if browser is IE
 */
export const isInternetExplorer = () =>
  /MSIE/.test(window.navigator.userAgent) ||
  /Trident/.test(window.navigator.userAgent);

export const escapeICSDescription = (description: string) =>
  description.replace(/(\r?\n|<br ?\/?>)/g, "\\n");

/**
 * Takes an event object and returns a Google Calendar Event URL
 */
function googleShareUrl(
  description: string,
  startDatetime: Date,
  title: string,
) {
  const url = `https://calendar.google.com/calendar/render?action=TEMPLATE&dates=${toLocaleUSDateString(startDatetime)}/${toLocaleUSDateString(new Date(startDatetime.getTime() + 86400000))}&text=${title}&details=${description}`;
  return url;
}

/**
 * Takes an event object and returns an array to be downloaded as ics file
 */
function buildShareFile(
  description: string,
  startDatetime: Date,
  title: string,
  url: string,
) {
  const content = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "BEGIN:VEVENT",
    `URL:${url}`,
    "METHOD:PUBLISH",
    `DTSTART;VALUE=DATE:${startDatetime.toISOString().split("T")[0].replace(/-/g, "")}`,
    `SUMMARY:${title}`,
    `DESCRIPTION:${escapeICSDescription(description)}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\n");

  return isMobile()
    ? encodeURI(`data:text/calendar;charset=utf8,${content}`)
    : content;
}

/**
 * Takes an event object and a type of URL and returns either a calendar event
 * URL or the contents of an ics file.
 * @param {string} event.description
 * @param {string} event.startDatetime
 * @param {string} event.title
 * @param {enum} type One of SHARE_SITES from ./enums
 */
export const buildShareUrl = (
  description: string,
  startDatetime: string | null | undefined,
  title: string,
  type: string,
  url?: string,
) => {
  const shouldEncode =
    type !== SHARE_SITES.ICAL && type !== SHARE_SITES.OUTLOOK;

  const data = {
    description: shouldEncode ? encodeURIComponent(description) : description,
    startDatetime: startDatetime,
    title: shouldEncode ? encodeURIComponent(title) : title,
  };

  const start = startDatetime ? new Date(startDatetime) : new Date();

  switch (type) {
    case SHARE_SITES.GOOGLE:
      return googleShareUrl(data.description, start, data.title);
    default:
      return buildShareFile(data.description, start, data.title, url || "");
  }
};

export function readParams(
  url: string,
  responseMode: "query" | "fragment" = "query",
): URLSearchParams {
  if (!url) throw new TypeError("Invalid URL");
  // the base URL is irrelevant, it's just here to support relative url arguments
  const parsedUrl = new URL(url, "http://127.0.0.1");
  const params = parsedUrl[responseMode === "fragment" ? "hash" : "search"];
  return new URLSearchParams(params.slice(1));
}

export function sortCommunications(
  a: ViewCommunicationResponse,
  b: ViewCommunicationResponse,
) {
  const sortOrder: CommunicationStatus[] = [
    CommunicationStatus.Complete,
    CommunicationStatus.ChangesRequested,
    CommunicationStatus.InProgress,
    CommunicationStatus.Open,
    CommunicationStatus.Pending,
    CommunicationStatus.Sent,
    CommunicationStatus.Unknown,
    CommunicationStatus.Closed,
    CommunicationStatus.Deleted,
  ];
  const indexA = sortOrder.indexOf(a.status ?? CommunicationStatus.Unknown);
  const indexB = sortOrder.indexOf(b.status ?? CommunicationStatus.Unknown);

  if (
    !a.lastActivityDate ||
    !b.lastActivityDate ||
    !a.createdDate ||
    !b.createdDate
  ) {
    return 0;
  }

  if (indexA - indexB === 0) {
    return (
      b.lastActivityDate?.getTime() ??
      b.createdDate.getTime() -
        (a.lastActivityDate?.getTime() ?? a.createdDate?.getTime())
    );
  }
  return indexA - indexB;
}

export function futureDeactivationDate(deactivationDate: string | undefined) {
  if (!deactivationDate) return "";

  const dateNum = Date.parse(deactivationDate.toString());

  if (dateNum) {
    const date = new Date(dateNum);
    const inFuture = new Date().toISOString() < date.toISOString();

    if (inFuture) {
      return ` until ${toLocaleUSDateString(date)}`;
    }
  }

  return "";
}

/**
 * Updates URL search parameters while preserving existing ones.
 * Specifically designed for search functionality that needs to maintain
 * tab state and other parameters while updating search terms.
 *
 * @param currentSearch - Current location.search string
 * @param updates - Object containing parameter updates
 * @param caseId - Case ID for the pathname
 * @returns Object with pathname and search string for navigation
 */
export function updateProductionTabParams(
  currentSearch: string,
  updates: {
    tab?: string;
    produceSearch?: string;
    withheldSearch?: string;
  },
  caseId: string,
): { pathname: string; search: string } {
  // Create new search params from current location to preserve all existing parameters
  const newSearchParams = new URLSearchParams(currentSearch);

  // Always set the tab parameter first if provided
  if (updates.tab) {
    newSearchParams.set(SEARCH_PARAMS.LABEL.TAB, updates.tab);
  }

  // Set or remove PRODUCE_SEARCH parameter based on search text
  if (updates.produceSearch !== undefined) {
    if (updates.produceSearch?.trim()) {
      newSearchParams.set(SEARCH_PARAMS.SEARCH, updates.produceSearch);
    } else {
      // Remove the parameter if search is empty
      newSearchParams.delete(SEARCH_PARAMS.SEARCH);
    }
  }

  // Set or remove WITHHELD_SEARCH parameter based on search text
  if (updates.withheldSearch !== undefined) {
    if (updates.withheldSearch?.trim()) {
      newSearchParams.set(
        SEARCH_PARAMS.WITHHELD_SEARCH,
        updates.withheldSearch,
      );
    } else {
      // Remove the parameter if search is empty
      newSearchParams.delete(SEARCH_PARAMS.WITHHELD_SEARCH);
    }
  }

  // Preserve all other existing parameters (view, folder IDs, etc.)
  // The newSearchParams already contains all existing parameters from currentSearch

  return {
    pathname: `/cases/${caseId}`,
    search: `?${newSearchParams.toString()}`,
  };
}

/*
 * Returns the first error message from zod field validation.
 * Useful with tanstack-form and the FormFieldError component.
 *
 * @example
 * <FormFieldError errorMessage={getFieldError(field)} />
 */
export function getFieldError(field: AnyFieldApi) {
  return field.state.meta.isTouched && !field.state.meta.isValid
    ? field.state.meta.errors[0]?.message
    : undefined;
}

/**
 * Gets the definition inclusion mode from user preferences.
 * Returns "always" if alwaysShowDefinitionsInRequests is true,
 * "never" if hideDefinitionsInRequests is true,
 * or "first" as the default.
 */
export function getDefinitionInclusionMode(
  user: UserResponse | undefined,
): DefinitionInclusionMode {
  const alwaysShow =
    user?.preferences?.documentPreferences?.alwaysShowDefinitionsInRequests ??
    false;
  const hide =
    user?.preferences?.documentPreferences?.hideDefinitionsInRequests ?? false;

  if (alwaysShow) return "always";
  if (hide) return "never";
  return "first";
}

/**
 * Returns the input.
 * This is useful for preserving environment variables in production builds,
 * especially in conditionals. This is important because we replace environment
 * variables after the build is complete, and Vite will remove conditionals
 * if it knows the value will always be the same.
 */
export const id = <T>(x: T): T | undefined => (Date.now() > 0 ? x : undefined);

export const checkProduceDocuments = () => {
  const objection21 = document.getElementById(
    `objection-${PRODUCE_DOCUMENTS_OBJECTION_ID}`,
  ) as HTMLInputElement;

  if (objection21) {
    if (!objection21.checked) {
      objection21.click();
    }
  }
};

/**
 *
 *
 * Azure Upload Utils
 *
 *
 */

export interface AzureUploadResult {
  // data: { [key: string]: any };
  data: CaseFileUploadBatch;
  percentCompleted: number;
}

export async function uploadFilesToAzure({
  files,
  caseId,
  type,
  caseFileApi,
  setEstimatedProgress,
}: {
  files: File[];
  caseId: string;
  type: number;
  caseFileApi: CaseFileApi;
  setEstimatedProgress?: (percent: number) => void;
}): Promise<AzureUploadResult> {
  try {
    // Step 1: Get SAS URL from CaseFileApi
    const sasUrl = await caseFileApi.caseFileGetUploadUrl({ caseId });
    const splitUrl = sasUrl.split("?");

    // Step 2: Generate random UUID for upload key
    const uploadKey = uuidv4();

    // Step 3: Upload files to Azure Blob Storage and collect file info
    const uploadedFiles: CaseFileUpload[] = [];
    let totalProgress = 0;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      // SAS url is ".../{containerName}?querystring=" we need to insert the key and filename so it looks like ".../{containerName}/{key}/{filename}?querystring="
      const blobUrl =
        splitUrl[0] +
        `/${uploadKey}/${encodeURIComponent(file.name)}` +
        "?" +
        splitUrl[1];

      // Create BlockBlobClient from the SAS URL
      const blockBlobClient = new BlockBlobClient(blobUrl);

      // Convert File to ArrayBuffer for upload
      const arrayBuffer = await file.arrayBuffer();

      // Upload the file
      const uploadResponse = await blockBlobClient.uploadData(arrayBuffer, {
        onProgress: (progress) => {
          const fileProgress = progress.loadedBytes
            ? Math.round((progress.loadedBytes / file.size) * 100)
            : 0;
          const overallProgress = Math.round(
            (i * 100 + fileProgress) / files.length,
          );
          setEstimatedProgress?.(overallProgress);
        },
        blobHTTPHeaders: {
          blobContentType: file.type,
        },
      });

      // Extract hash from response headers
      const hash = uploadResponse.contentMD5
        ? Buffer.from(uploadResponse.contentMD5).toString("base64")
        : "";

      // Add to uploaded files array
      uploadedFiles.push({
        name: file.name,
        key: uploadKey,
        hash: hash || file.size.toString(), // On files larger than 100MB Azure doesn't generate a hash *sigh*
      });

      totalProgress = Math.round(((i + 1) * 100) / files.length);
      setEstimatedProgress?.(totalProgress);
    }

    // Step 4: Post uploaded file info to CaseFileApi HandleUpload method
    const handleUploadResponse = await caseFileApi.caseFileHandleUpload({
      caseId,
      type,
      caseFileUpload: uploadedFiles,
    });

    return {
      data: handleUploadResponse,
      percentCompleted: 100,
    };
  } catch (error) {
    throw new Error(
      `Upload failed: ${error instanceof Error ? error.message : "Unknown error"}`,
    );
  }
}

/* building url paths for file explorer */
export function folderDirectory(
  nextFolderId: string,
  position: number,
  firstFolderId: string,
  secondFolderId: string,
) {
  if (position === 0) {
    return nextFolderId;
  }
  if (position === 1) {
    return `${firstFolderId}/${nextFolderId}`;
  }
  return `${firstFolderId}/${secondFolderId}/${nextFolderId}`;
}

export function getHistoryPath(
  variant: FileExplorerVariant,
  documentId: string,
  currentStep: string,
  caseId: string,
) {
  return variant === FILE_EXPLORER_VARIANTS.WIZARD
    ? `/wizard/${documentId}/${currentStep}/${caseId}`
    : `/cases/${caseId}`;
}

export function getSearchParamsVariables(
  variant: FileExplorerVariant,
  interrogatoryId: number,
  filter: string | null,
  search: string | null,
  number?: string | null,
) {
  return variant === FILE_EXPLORER_VARIANTS.WIZARD
    ? `?tab=all&review=true${number ? `&number=${number}` : ""}&interrogatoryId=${interrogatoryId}${filter ? `&filter=${filter}` : ""}${search ? `&search=${encodeURIComponent(search)}` : ""}`
    : `?tab=production${search ? `&search=${encodeURIComponent(search)}` : ""}`;
}

/* case id for file explorer based on variant */
export function getCaseId(
  variant: FileExplorerVariant,
  urlParams: { id: string; caseId: string },
) {
  return variant === FILE_EXPLORER_VARIANTS.WIZARD
    ? urlParams.caseId
    : urlParams.id;
}

/**
 * Checks if a user can use a specific toggleable feature.
 * Returns true unless the feature is in the user's disabledFeatures list.
 *
 * @param user - The user object to check
 * @param feature - The toggleable feature to check
 * @returns true if the user can use the feature, false if it's disabled
 */
export function userCanUseFeature(
  user: User,
  feature: ToggleableFeature,
): boolean {
  return !user.disabledFeatures.has(feature);
}

export function externalCaseFilterCallback(
  option: Option,
  props: TypeaheadPropsAndState,
  user: UserResponse | undefined,
  externalCases: ExternalCaseResponse[] | undefined,
): boolean {
  // This is a hack-around to check if the user has typed in a value that matches with the title of a firm case or the name of a client of a firm case.
  // This simulates what the typeahead component does when the user types in a value that matches with the title of a firm case or the name of a client of a firm case.
  // We have to do this since the typeahead component does not provide the results it displays based on matching letters the user types.
  // We then then have a `matches` boolean that is true or false. If its true, that means we have filtered backend results not being displayed in the typeahead component
  // That we should display, therefore, we return true to show those results.
  const isTypeaheadMatchingCases = props.options.some((c: Option) =>
    (c as CombinedCaseSearch).title
      .toLowerCase()
      .includes(props.text.toLowerCase()),
  );
  const matches = externalCases?.filter(
    (c) =>
      c.title.toLowerCase().includes(props.text.toLowerCase()) ||
      c.clients?.some((client) =>
        client?.name?.toLowerCase()?.includes(props.text.toLowerCase()),
      ),
  );
  if (
    user?.externalConnection?.isActive &&
    !isTypeaheadMatchingCases &&
    matches &&
    matches.length > 0
  ) {
    return true;
  }
  return (option as CombinedCaseSearch).title
    .toLowerCase()
    .includes(props.text.toLowerCase());
}
export interface hasTagId {
  tagIds?: number[] | null | undefined;
}

function getTagsInUse(
  tg: hasTagId[],
  allTags: Map<number, string> | undefined,
) {
  const tags: number[] = [];
  for (const tq of tg) {
    for (const tgId of tq.tagIds ?? []) {
      if (!tags.includes(tgId)) {
        tags.push(tgId);
      }
    }
  }
  return Array.from(allTags?.entries() ?? []).filter((at) =>
    tags.includes(at[0]),
  );
}

export function getCaseTypeDisplay(active: string[], jx: Jurisdiction) {
  let ret = getCases(jx).find((x) => x.id === active[0])?.name;

  if (active.length > 1) {
    ret += ` (+${active.length - 1})`;
  }
  return ret;
}

export function getTagsDisplay(
  active: unknown[],
  tags: Map<number, string> | undefined,
) {
  if (!active.length || !tags) {
    return undefined;
  }

  const firstTagId = Number(active[0]);
  let ret = tags.get(firstTagId);

  if (active.length > 1) {
    ret += ` (+${active.length - 1})`;
  }

  return ret;
}

export function getSortedTags(
  hasTag: hasTagId[],
  availableTags: Map<number, string> | undefined,
  tagFilter: unknown[],
) {
  if (!availableTags) {
    return [];
  }

  const tagsInUse = getTagsInUse(hasTag ?? [], availableTags);

  return tagsInUse
    .sort((a, b) => {
      const aSelected = tagFilter?.includes(a[0]);
      const bSelected = tagFilter?.includes(b[0]);

      if (aSelected && !bSelected) {
        return -1;
      }

      if (bSelected && !aSelected) {
        return 1;
      }

      return a[1].localeCompare(b[1]);
    })
    .map((tg) => ({ name: tg[1] ?? "", id: tg[0].toString() ?? "" }));
}

export interface hasCaseTypeId {
  caseTypeId?: string | null | undefined;
}

export function getCaseTypesInUse(cs: hasCaseTypeId[], jx: Jurisdiction) {
  const caseId: (string | undefined)[] = [];
  for (const tq of cs) {
    if (!caseId.includes(tq.caseTypeId ?? undefined)) {
      caseId.push(tq.caseTypeId ?? undefined);
    }
  }
  return getCases(jx).filter((c) => caseId.includes(c.id));
}

export function getCases(jurisdiction: Jurisdiction) {
  const cases =
    jurisdiction?.caseClasses
      ?.filter((c) => c.subTypes)
      .flatMap((c) => [c.subTypes ?? []])
      .flat()
      ?.map((cc) => ({ name: cc.shortName ?? "", id: cc.id ?? "" })) ?? [];
  const extra =
    jurisdiction?.caseClasses?.map((c) => ({
      name: c.name ?? "",
      id: c.id ?? "",
    })) ?? [];
  return [...extra, ...cases];
}

export function getTagsKeys(
  tagIds: number[] | null | undefined,
  availableTags?: Map<number, string>,
) {
  if (!tagIds?.length) {
    return null;
  }

  return tagIds
    ?.sort()
    ?.map((t) => (availableTags ? availableTags.get(t) : ""))
    .join("-");
}

export function caseTypeDisplay(
  caseTypeId: string | null | undefined,
  jurisdiction: JurisdictionResponse | undefined,
  includeClass: boolean,
  noCaseText = NO_CASE_TEXT,
) {
  if (!caseTypeId) {
    return noCaseText;
  }
  let typeClass = jurisdiction?.caseClasses?.find((x) => x.id === caseTypeId);

  if (typeClass) {
    return typeClass.name;
  }

  typeClass = jurisdiction?.caseClasses?.find((x) =>
    x.subTypes?.find((s) => s.id === caseTypeId),
  );

  if (!typeClass) {
    return "Unknown";
  }

  const type = typeClass?.subTypes?.find((x) => x.id === caseTypeId);

  return `${includeClass && `${typeClass.name} - `}${type?.shortName}`;
}

/**
 * Transforms API response data to ensure it's an array when the API returns a single object.
 * This is useful for APIs that return inconsistent data structures.
 *
 * @param jsonData - The raw JSON response from the API
 * @returns The transformed response with data as an array
 */
export function transformSingleObjectToArray<T>(
  jsonData: { data: T | T[] } & Record<string, unknown>,
): { data: T[] } & Record<string, unknown> {
  if (
    jsonData &&
    typeof jsonData.data === "object" &&
    !Array.isArray(jsonData.data)
  ) {
    return {
      ...jsonData,
      data: [jsonData.data], // Wrap the single object in an array
    };
  }

  return jsonData as { data: T[] } & Record<string, unknown>;
}

export function getDocumentStatusText(status: DocumentStatus) {
  switch (status) {
    case DocumentStatus.Unknown:
      return { title: "Unknown", status: "Unknown" };
    case DocumentStatus.Uploaded:
      return { title: "Uploaded", status: "Uploaded" };
    case DocumentStatus.Processing:
      return { title: "Processing", status: "Processing" };
    case DocumentStatus.ParsingComplete:
      return { title: "Parsing Complete", status: "Processing" };
    case DocumentStatus.SplittingComplete:
      return { title: "Splitting Complete", status: "Processing" };
    case DocumentStatus.Processed:
      return { title: "Processed", status: "Processed" };
    case DocumentStatus.InfoConfirmed:
      return { title: "Info Confirmed", status: "In Progress" };
    case DocumentStatus.Complete:
      return { title: "Complete", status: "Complete" };
    case DocumentStatus.Deleted:
      return { title: "Deleted", status: "Deleted" };
    case DocumentStatus.Error:
      return { title: "Error", status: "Error" };
    default:
      return { title: "Unknown", status: "Unknown" };
  }
}

export function triggerDownloadViaIframe(url: string) {
  const iframe = document.createElement("iframe");
  iframe.style.display = "none";
  iframe.src = url;
  document.body.appendChild(iframe);
  // Removing the iframe won't cancel an in-progress download — once the browser's
  // download manager picks up the request, the iframe is no longer involved.
  setTimeout(() => iframe.remove(), 60_000);
}

export function getUploadingDocument(name: string): DocumentClient {
  return {
    id: `${name}-${Math.random()
      .toString(36)
      .replace(/[^a-z]+/g, "")
      .slice(0, 5)}`,
    name,
    info: {
      documentType: DocumentType.Unknown,
      documentSubType: DocumentSubType.Unknown,
      venue: "",
      jurisdiction: "",
      propoundingParty: PropoundingParty.Unknown,
      propoundingPartyName: "",
      respondingPartyName: "",
      caseNumber: "",
      setNumber: 1,
      startingNumber: 1,
    },
    uploadDate: new Date(),
    status: DocumentStatus.Unknown,
    isArchived: false,
  } as DocumentClient;
}

export function leadAttorneyName(
  _case: Case | undefined,
  users: UserResponse[],
) {
  if (_case) {
    let primary = _case.attorneys?.find((x) => x.isPrimary);
    if (!primary) {
      primary = _case.attorneys?.at(0);
    }

    if (primary) {
      const attorney = users?.find((x) => x.id === primary?.id);
      if (attorney) {
        return `${attorney.lastName}, ${attorney.firstName}`;
      }
    }
  }
  return "-";
}

/* main date formatting function */
export function toLocaleUSDateString(
  date: Date | undefined | null | string,
  options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    timeZone: "UTC",
  },
) {
  if (!date) return "";
  if (typeof date === "string") {
    return new Date(Date.parse(date)).toLocaleDateString("en-US", options);
  }
  return date.toLocaleDateString("en-US", options);
}

export function getQuestionType(document: DocumentDetailClient) {
  return document.info?.documentSubType ===
    DocumentSubType.RequestsForAdmission ||
    document.info?.documentSubType === DocumentSubType.RequestsForProduction
    ? "Request"
    : "Interrogatory";
}

export function getCaseJurisdiction(
  cases: Case[] | CaseResponse[] | undefined,
  caseId: string,
) {
  if (!caseId || !cases) return undefined;
  return cases.find((c) => c.id === caseId)?.jurisdiction;
}
